import type {
  ProductUpdateRequestType,
  ProductUpdateResponseType,
} from "@sellar-io/api-v2/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import { apiV2AssertResponse, apiV2Request } from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";

export const useProductUpdate = (productId?: number) => {
  const queryClient = useQueryClient();
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  return useMutation({
    mutationFn: (
      data: Omit<ProductUpdateRequestType, "active"> & { active?: never },
    ) =>
      apiV2Request({
        user,
        business,
        method: "put",
        path: `products/${productId}`,
        data,
      }).then(
        (response) =>
          apiV2AssertResponse(response).data as ProductUpdateResponseType,
      ),

    onSettled: (response) => {
      queryClient.invalidateQueries({
        queryKey: [business?.id, ServerStateKeys.Products, productId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          business?.id,
          ServerStateKeys.Products,
          productId,
          "supplier",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [user?.uid, business?.id, ServerStateKeys.Products],
      });
      queryClient.invalidateQueries({
        queryKey: [user?.uid, business?.id, ServerStateKeys.StockOverview],
      });
      if (response?.ParentId) {
        //sometimes we pull data from the parent, so we need to invalidate that too
        queryClient.invalidateQueries({
          queryKey: [business?.id, ServerStateKeys.Products, response.ParentId],
        });
        queryClient.invalidateQueries({
          queryKey: [
            business?.id,
            ServerStateKeys.Products,
            response.ParentId,
            "supplier",
          ],
        });
      }
    },
  });
};
